import axios, { type AxiosResponse } from "axios";
import { filterEmptyProps } from "@/helpers/helpers";
import axiosInstance from "@/helpers/axiosInstance";
import { isNil, isEmpty } from "lodash";
import { ApiResponse } from "@/types/common.types";
import { FormattedAuthUser, User } from "@/types/user.types";
import { UUID } from "crypto";
import { cstMoment } from "@common/utils/date.utils";
import type { PendingPuzzleGuessForApi } from "@/helpers/puzzlePendingGuessesStorage";

const apiEndpoint = "/api/users";

// Type definitions

/** Streak snapshot on POST /api/user-guesses for puzzle mode (matches server `StreakUpdateResult`). */
export type UserGuessSubmitStreak = {
    currentStreak: number;
    previousStreak: number;
    streakChangeType: "incremented" | "reset" | "already_completed";
};

export type SubmitUserGuessResponseBody = {
    userGuess?: unknown;
    score?: unknown;
    streak?: UserGuessSubmitStreak | null;
};

export interface UserGuessParams {
    userId: number;
    propertyId: string;
    playDate: string;
    userPropertyId: string;
    playablePropertyId: string;
    version: string;
}

export interface UserScoreResponse {
    daily_score: {
        scores: any[];
        total_score: number;
    };
    [key: string]: any;
}

export interface SubmitGuessParams {
    userId: string;
    propertyId: string;
    guess: number;
    challengeId: string;
    userPropertyId?: string | null;
    shortLink: string;
    version?: string;
    is_archive_play?: boolean;
}

export interface UserGuessesPerPeriodParams {
    user_id: string;
    date?: string;
    is_challenge?: boolean;
}

export interface CreateCommentParams {
    userId: string;
    propertyId: string;
    content: string;
    parentId?: string | null;
}

export interface CreateGuessParams {
    userId: string;
    propertyId: string;
    prediction: number;
    guess: number;
    reason: string;
}

export interface UserProfileHeader {
    nickname: string;
    displayName: string | null;
    profilePhotoUrl: string | null;
    bio: string | null;
    profilePrivate: boolean;
    showScoreLeaderboard?: boolean;
    notificationSettings?: UserNotificationSettingUpdate[];
}

export interface UserProfileStats {
    solved: number;
    averageScore: number | null;
    currentStreak: number;
}

export type UserNotificationMessageType =
    | "new_listings_available"
    | "new_user_tips"
    | "user_awards"
    | "user_referral"
    | "new_daily_puzzle"
    | "comment_reply";

export type UserNotificationFrequency = "daily" | "weekly" | "never";

export interface UserNotificationSettingUpdate {
    messageType: UserNotificationMessageType;
    emailFrequency: UserNotificationFrequency;
    pushFrequency: UserNotificationFrequency;
}

export interface AgentProfileDataUpdate {
    brokerage?: string;
    phone?: string;
    website?: string;
    instagram?: string;
    tiktok?: string;
    facebook?: string;
    youtube?: string;
    x?: string;
}

/**
 * Payload for updating a user's profile via PATCH /users/:userId/profile.
 * All fields are optional – omit anything that should remain unchanged.
 */
export interface UpdateUserProfilePayload {
    displayName?: string;
    nickname?: string;
    bio?: string;
    showScoreLeaderboard?: boolean;
    profilePrivate?: boolean;
    notificationSettings?: UserNotificationSettingUpdate[];
    /**
     * Agent-only profile fields. Backend silently ignores this object for non‑agent users,
     * so it is safe to always include from the client side.
     */
    agentData?: AgentProfileDataUpdate;
}

export interface UserProfileActivityItem {
    playablePropertyId: string;
    shortLink: string | null;
    streetAddress?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    beds?: number | null;
    baths?: number | null;
    sqft?: number | null;
    listPrice?: number | null;
    actualPrice: number;
    propertyImage?: string | null;
    datePlayed: string;
    guesses: number[];
    /** From API when viewing another user; drives blur / gate on the card. */
    viewerHasPlayed: boolean;
}

/**
 * Sent as `viewerNickname` when loading someone else's activity without a logged-in viewer.
 * Must be truthy and non-matching any real user so the API returns viewerHasPlayed=false for every card.
 */
export const GUEST_PROFILE_VIEWER_NICKNAME = "__guest_viewer__";

export interface UserProfileActivityResponse {
    total: number;
    currentPage: number;
    totalPages: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    nextPage: number | null;
    previousPage: number | null;
    data: UserProfileActivityItem[];
}

/**
 * Function to send user data to the server to find or create a user record.
 */
export async function findOrCreateUser(
    user: FormattedAuthUser,
    guestData: any = null,
    trackingData?: { referringUrl?: string; source?: string; location?: string },
    pendingGuesses?: PendingPuzzleGuessForApi[],
): Promise<ApiResponse<any>> {
    return axios.post(`${apiEndpoint}/find-or-create`, {
        userData: user,
        guestData,
        ...(trackingData || {}),
        ...(pendingGuesses && pendingGuesses.length > 0 ? { pendingGuesses } : {}),
    });
}

/**
 * Asynchronously synchronizes user data with the server by sending a PATCH request.
 */
export async function syncUser(userData: { id: string; [key: string]: any }): Promise<ApiResponse<any>> {
    const { id } = userData;
    return axios.patch(`${apiEndpoint}/${id}`, userData);
}

/**
 * Updates a user's profile.
 *
 * PATCH /users/:userId/profile
 *
 * Notes:
 * - All fields are optional; omit anything that hasn't changed.
 * - On nickname conflict, the API responds with 400 and
 *   { message: "The nickname is already in use." }.
 */
export async function updateUserProfile(userId: string, payload: UpdateUserProfilePayload): Promise<User> {
    return (await axiosInstance.patch(`${apiEndpoint}/${userId}/profile`, payload)).data;
}

/**
 * Uploads a user profile photo and returns the stored CDN URL.
 *
 * POST /users/:userId/profile-image
 */
export async function uploadUserProfileImage(userId: string, file: File): Promise<{ profilePhotoUrl: string }> {
    const formData = new FormData();
    formData.append("file", file);
    return (await axiosInstance.post(`${apiEndpoint}/${userId}/profile-image`, formData)).data;
}

/**
 * Asynchronously fetches daily properties data from the server based on the specified user ID.
 * For guest users with a location, uses the nearest properties endpoint.
 */
export const getDailyProperties = async (
    userId: string,
    isAuthenticated: boolean,
    generateIfEmpty: boolean = false,
    location?: string,
): Promise<ApiResponse<any>> => {
    if (!isAuthenticated) {
        // For guest users with location, use nearest properties endpoint
        if (location && location.trim()) {
            const query = new URLSearchParams({
                location,
                limit: "3",
            }).toString();
            const response = await axiosInstance.get(`/api/properties/nearest?${query}`);
            // Transform nearest-properties response to match getDailyProperties shape (wrap properties array in nested data object)
            return {
                data: {
                    data: response.data.properties || [],
                },
            };
        }
        // Fallback to default guest properties (week/year based)
        return await axiosInstance.get(`/api/properties/daily/guest`);
    }
    return await axiosInstance.get(`/api/user-properties/users/${userId}/properties/daily?generate=${generateIfEmpty}`);
};

/**
 * Asynchronously fetches a property data from the server.
 */
export const getRandomProperty = async (): Promise<ApiResponse<any>> => {
    return await axiosInstance.get(`/api/properties/daily/random`);
};

/**
 * Asynchronously retrieves user data for the specified user ID from the server.
 */
export async function findUser(userId: string): Promise<User> {
    return (await axios.get(`${apiEndpoint}/${userId}`)).data;
}

/**
 * Retrieves the notification settings for a user based on the provided user ID.
 */
export async function findUserNotificationSetting(userId: string): Promise<any> {
    return (await axios.get(`/api/user-notification-settings/users/${userId}/notifications`)).data;
}

/**
 * Retrieves the awards for a user based on the provided user ID.
 */
export async function getAwards(userId: string): Promise<any> {
    return (await axios.get(`/api/user-trophies/users/${userId}`)).data;
}

/**
 * Fetches profile header data for a user by nickname.
 */
export async function getUserProfileHeader(nickname: string): Promise<UserProfileHeader> {
    const encoded = encodeURIComponent((nickname ?? "").trim());
    return (await axiosInstance.get(`${apiEndpoint}/${encoded}/profile/header`)).data;
}

/**
 * Fetches profile stats for a user by nickname.
 */
export async function getUserProfileStats(nickname: string): Promise<UserProfileStats> {
    const encoded = encodeURIComponent((nickname ?? "").trim());
    return (await axiosInstance.get(`${apiEndpoint}/${encoded}/profile/stats`)).data;
}

/**
 * Fetches paginated profile activity for a user by nickname.
 * Pass the viewer's nickname: on your own profile it satisfies owner/private bypass; on someone else's profile it identifies the viewer for viewerHasPlayed.
 * Use {@link GUEST_PROFILE_VIEWER_NICKNAME} when the viewer is not logged in (anonymous).
 */
export async function getUserProfileActivity(
    nickname: string,
    options: { page?: number; limit?: number; viewerNickname?: string } = {},
): Promise<UserProfileActivityResponse> {
    const encoded = encodeURIComponent((nickname ?? "").trim());
    const page = String(options.page ?? 1);
    const limit = String(options.limit ?? 12);
    const params = new URLSearchParams({ page, limit });
    const viewer = options.viewerNickname?.trim();
    if (viewer) {
        params.set("viewerNickname", viewer);
    }
    return (await axiosInstance.get(`${apiEndpoint}/${encoded}/profile/activity?${params}`)).data;
}

/**
 * Fetches the user's guess data for a specific property ID.
 */
export async function getUserGuess(
    userId: number,
    propertyId: string,
    playDate: string,
    userPropertyId: string,
    playablePropertyId: string,
    version: string,
): Promise<any> {
    return (
        await axios.get(`/api/user-guesses/${userId}/guess`, {
            params: {
                propertyId,
                gameVersion: version,
                createdAt: playDate,
                userPropertyId,
                playablePropertyId,
            },
        })
    ).data;
}

/**
 * Get the daily score for a specific user and area.
 */
export async function getUserScore(
    userId: string,
    areaId: UUID,
    listScores: boolean = false,
): Promise<UserScoreResponse> {
    return (
        await axios.get(`/api/scores/${userId}/daily`, {
            params: {
                areaId,
                listScores,
            },
        })
    ).data;
}

/**
 * Retrieves the weekly score data for a specific user and area.
 */
export async function getWeeklyScore(
    userId: string,
    areaId: string,
    date: string = new Date().toISOString(),
    areaScope: string = "local",
): Promise<any> {
    return (
        await axios.get(`/api/scores/${userId}/weekly`, {
            params: {
                date: cstMoment(date).toISOString(),
                areaId,
                areaScope,
            },
        })
    ).data;
}

/**
 * Retrieves the weekly rank data for a specific user and area.
 */
export async function getWeeklyRank(userId: string, areaId: string, areaScope: string = "local"): Promise<any> {
    return (
        await axios.get(`/api/user-trophies/users/${userId}/weekly`, {
            params: {
                areaId,
                areaScope,
            },
        })
    ).data;
}

/**
 * Retrieves the daily rank data for a specific user and area.
 */
export async function getDailyRank(userId: string, areaId: string, areaScope: string): Promise<any> {
    return (
        await axios.get(`/api/user-trophies/users/${userId}/daily`, {
            params: {
                date: cstMoment().toISOString(),
                areaId,
                areaScope,
            },
        })
    ).data;
}

/**
 * Submits a guess for a property.
 */
export async function submitGuess(payload: {
    userId: string;
    propertyId: string;
    guessScore: { guess: number | number[]; score: number; areaId?: string; excludeScore?: boolean };
    challengeId?: string | null;
    userPropertyId?: string | null;
    shortLink?: string;
    version?: string;
    isArchivePlay?: boolean;
}): Promise<AxiosResponse<SubmitUserGuessResponseBody>> {
    const {
        userId,
        propertyId,
        guessScore,
        challengeId = null,
        userPropertyId = null,
        shortLink = "",
        version = "classic",
        isArchivePlay = false,
    } = payload;

    return await axios.post<SubmitUserGuessResponseBody>(`/api/user-guesses`, {
        userId,
        guessScore,
        gameVersion: version,
        isArchivePlay,
        ...(propertyId != null && { propertyId }),
        ...(challengeId != null && { challengeId }),
        ...(userPropertyId != null && { userPropertyId }),
        ...(shortLink != null && { shortLink }),
    });
}

/**
 * Get rankings
 */
export async function getRankings(areaId: string, period: string, areaScope: string = "local"): Promise<any> {
    const timeZoneOffset = new Date().getTimezoneOffset();
    const query = new URLSearchParams({
        areaId,
        period,
        date: cstMoment().toDatabaseDate(), // Use YYYY-MM-DD format instead of ISO string to match backend expectations
        timeZoneOffset: timeZoneOffset.toString(),
        areaScope,
    }).toString();
    return await axios.get(`${apiEndpoint}/top?${query}`);
}

/**
 * Get all scores per period
 */
export async function getScores(
    userId: string,
    areaId: string,
    dateTo: string,
    dateFrom: string,
    areaScope: string,
): Promise<any> {
    const endMoment = cstMoment(dateTo, "M/DD/YYYY").endOf("isoWeek"); // Sunday
    const end = endMoment.toDatabaseDate();
    const start = endMoment.clone().startOf("isoWeek").toDatabaseDate(); // Monday

    const payload = {
        userId,
        areaId,
        dateTo: end,
        dateFrom: start,
        areaScope,
    };

    const filteredPayload = filterEmptyProps(payload);
    const query = new URLSearchParams(filteredPayload).toString();
    return await axios.get(`/api/scores/${userId}?${query}`);
}

/**
 * Get streaks
 */
export async function getStreaks(userId: string): Promise<any> {
    return await axios.get(`/api/streaks/${userId}`);
}

/**
 * Get title text for a user.
 */
export async function getTitleText(userId: string): Promise<any> {
    return (await axios.get(`${apiEndpoint}/${userId}/title`)).data;
}

/**
 * Get share text for a user.
 */
export async function getShareText(userId: string): Promise<any> {
    return (await axios.get(`${apiEndpoint}/${userId}/share`)).data;
}

/**
 * Get user guesses per period
 */
export async function getUserGuessesPerPeriod({
    userId,
    date,
    isChallenge,
    gameVersion,
}: {
    userId: string;
    date?: string;
    isChallenge?: boolean;
    gameVersion?: string;
}): Promise<any> {
    const payload: any = {
        date,
        isChallenge,
        gameVersion,
    };

    // If date is empty then delete from payload
    if (!date) {
        delete payload.date;
    }

    // Remove undefined fields
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

    const query = new URLSearchParams(payload).toString();
    return await axios.get(`/api/user-guesses/${userId}/guesses/period?${query}`);
}

export async function getUserChallengeResults(userId: string): Promise<any> {
    return await axios.get(`/api/challenges/users/${userId}/challenges/results`);
}

export async function setUserViewedChallengeScores(viewedChallengeScoreIds: string[]): Promise<any> {
    return await axios.post(`/api/scores/viewed-challenge-scores`, {
        viewedChallengeScoreIds,
    });
}

export async function getUserChallengesHistory(userId: string): Promise<any> {
    return await axios.get(`/api/challenges/users/${userId}/challenges/history`);
}

export async function getUserPuzzleStats(userId: string): Promise<any> {
    return await axios.get(`${apiEndpoint}/${userId}/puzzle/stats`);
}

export async function getUserPuzzleGuessDistribution(userId: string): Promise<any> {
    return await axios.get(`${apiEndpoint}/${userId}/puzzle/guess-distribution`);
}

/**
 * Fetches user comments for a specific user with pagination support
 */
export async function getUserComments(userId: string, page: number = 1, limit: number = 10): Promise<any> {
    const query = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
    }).toString();
    return (await axiosInstance.get(`/api/comments/user/${userId}?${query}`)).data;
}

/**
 * Creates a new comment for a property
 */
export async function createComment(
    userId: string,
    propertyId: string,
    content: string,
    parentId: string | null = null,
): Promise<any> {
    const payload: any = {
        userId,
        content,
        ...(!isNil(parentId) && !isEmpty(parentId) && { parentId }),
    };
    return (await axios.post(`/api/comments/Property/${propertyId}`, payload)).data;
}

/**
 * Fetches comments for a specific property with pagination support
 */
export async function getPropertyComments(propertyId: string, page: number = 1, limit: number = 10): Promise<any> {
    const query = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
    }).toString();
    return (await axiosInstance.get(`/api/comments/Property/${propertyId}?${query}`)).data;
}

export async function createGuess(
    userId: string,
    propertyId: string,
    prediction: number,
    guess: number,
    reason: string,
): Promise<any> {
    return (
        await axios.post(`/api/user-guesses/${userId}/guess/social`, {
            propertyId,
            prediction,
            guess,
            reason,
        })
    ).data;
}

export async function getUserSocialListings(userId: string, page: number = 1, limit: number = 10): Promise<any> {
    const query = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
    }).toString();
    return (await axiosInstance.get(`/api/users/${userId}/social/listings?${query}`)).data;
}

/**
 * Retrieves the social points for a user based on the provided user ID.
 */
export async function getUserSocialPoints(userId: string): Promise<any> {
    return (await axiosInstance.get(`${apiEndpoint}/${userId}/social/points`)).data;
}

export async function deleteUserSocialGuess(userId: string, listingId: string): Promise<any> {
    return (await axios.delete(`/api/user-guesses/${userId}/social/listing/${listingId}/guesses`)).data;
}

/**
 * Delete a comment
 */
export async function deleteComment(userId: string, commentId: string): Promise<any> {
    const response = await axios.delete(`/api/comments/user/${userId}/${commentId}`);
    return response.data;
}

/**
 * Fetches the first top-level comment made by a specific user on a commentable entity.
 */
export async function getUserFirstComment(
    userId: string,
    commentableType: string = "Property",
    commentableId: string,
): Promise<any> {
    const response = await axios.get(`/api/comments/user/${userId}/${commentableType}/${commentableId}/first-comment`);
    return response.data;
}

/**
 * Updates the last visit time of a user and resets inactivity email flag.
 */
export async function updateUserLastVisit(userId: string, lastVisitedAt: string): Promise<any> {
    const response = await axios.patch(`/api/users/${userId}/last-visit`, {
        lastVisitedAt,
    });
    return response.data;
}
